import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Idle-timeout choices in minutes. 0 = disabled.
 */
export const IDLE_TIMEOUT_OPTIONS = [0, 1, 5, 15, 30] as const;
export type IdleTimeoutMin = (typeof IDLE_TIMEOUT_OPTIONS)[number];

interface LockState {
  /** Base64-encoded WebAuthn credential ID, null when biometric isn't enrolled. */
  credentialId: string | null;
  /** Minutes of inactivity before auto-lock. 0 disables auto-lock. */
  idleTimeoutMin: IdleTimeoutMin;
  /** True when the UI is currently gated by the lock screen. */
  isLocked: boolean;

  enroll: (credentialId: string) => void;
  unenroll: () => void;
  lock: () => void;
  unlock: () => void;
  setIdleTimeout: (m: IdleTimeoutMin) => void;
}

const DEFAULTS = {
  credentialId: null as string | null,
  idleTimeoutMin: 5 as IdleTimeoutMin,
  isLocked: false,
};

export const useLockStore = create<LockState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,
      enroll: (credentialId) => set({ credentialId }),
      unenroll: () => set({ credentialId: null, isLocked: false }),
      lock: () => {
        // No point locking when there's no credential to unlock with.
        if (!get().credentialId) return;
        set({ isLocked: true });
      },
      unlock: () => set({ isLocked: false }),
      setIdleTimeout: (m) => set({ idleTimeoutMin: m }),
    }),
    {
      name: 'hoopla-pos:lock',
      version: 1,
      // Defensive: future field additions won't wipe biometric enrollment.
      migrate: (persisted) => {
        const prev = (persisted ?? {}) as Partial<LockState>;
        return { ...DEFAULTS, ...prev };
      },
    },
  ),
);
